import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Card,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { CheckCircleOutline, ContentCopy } from "@mui/icons-material";
import toast from "react-hot-toast";
import LoginSlider from "../Login/LoginSlider";
import layoutStyles from "../Login/login.module.scss";
import textStyles from "components/shared/css/text.module.scss";
import uiStyles from "./twoFactorSetup.module.scss";
import LogoIcon2 from "static/svgs/logo2";
import CustomButton from "components/shared/Buttons/CustomButton";
import {
  fetchDeviceTwoFactorSetupService,
  verifyDeviceTwoFactorService,
} from "../../../redux/services/auth.service";

const TwoFactorSetupContainer = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const [initialLoading, setInitialLoading] = useState(true);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  const [manualKey, setManualKey] = useState("");
  const [qrCode, setQrCode] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [verified, setVerified] = useState(false);
  const [userDetails, setUserDetails] = useState({ username: "", email: "" });
  const appBaseUrl = useMemo(() => {
    if (process.env.REACT_APP_APP_URL) {
      return process.env.REACT_APP_APP_URL;
    }
    if (typeof window !== "undefined" && window.location?.origin) {
      return window.location.origin;
    }
    return "/";
  }, []);

  const fetchSetupDetails = useCallback(async () => {
    if (!uuid) {
      setServerError("Setup link is invalid.");
      setInitialLoading(false);
      return;
    }

    setServerError("");
    setInitialLoading(true);
    try {
      const response = await fetchDeviceTwoFactorSetupService(uuid);
      const payload = response?.data || response;

      setManualKey(payload?.manualKey || "");
      setQrCode(payload?.qrCode || "");
      setVerified(Boolean(payload?.twoFactorEnabled));
      setUserDetails({
        username: payload?.username || "",
        email: payload?.email || "",
      });
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to load setup details.";
      setServerError(message);
    } finally {
      setInitialLoading(false);
    }
  }, [uuid]);

  useEffect(() => {
    fetchSetupDetails();
  }, [fetchSetupDetails]);

  const steps = useMemo(
    () => [
      "Install the Google Authenticator app on your mobile device.",
      "Open the app and scan the QR code below, or enter the manual key.",
      "Enter the 6-digit code from the app to finish the setup.",
    ],
    []
  );

  const handleCodeChange = (event) => {
    const digitsOnly = event.target.value.replace(/[^0-9]/g, "").slice(0, 6);
    setVerificationCode(digitsOnly);
  };

  const handleCopyManualKey = async () => {
    if (!manualKey) return;
    try {
      await navigator.clipboard.writeText(manualKey);
      toast.success("Manual key copied to clipboard");
    } catch (error) {
      toast.error("Failed to copy manual key");
    }
  };

  const handleVerify = async (event) => {
    event.preventDefault();
    if (verificationCode.length !== 6) {
      setServerError("Enter the 6-digit code from Google Authenticator.");
      return;
    }

    setServerError("");
    setVerificationLoading(true);
    try {
      await verifyDeviceTwoFactorService({
        uuid,
        token: verificationCode,
      });
      setVerified(true);
      setVerificationCode("");
      toast.success("Two-factor authentication enabled successfully");
      setTimeout(() => {
        window.location.href = appBaseUrl;
      }, 1000);
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to verify authenticator code.";
      setServerError(message);
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleNavigateToLogin = useCallback(() => {
    navigate("/login");
  }, [navigate]);

  return (
    <div className={layoutStyles.container}>
      <div className={`${layoutStyles.left} ${uiStyles.leftPanel}`}>
        <LogoIcon2 />
        <div className={`${layoutStyles.loginBox} ${uiStyles.loginBoxFull}`}>
          <Card className={`${layoutStyles.formContainer} ${uiStyles.setupCard}`}>
            {initialLoading ? (
              <Box className={uiStyles.loadingContainer}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary" mt={2}>
                  Preparing your authenticator setup guide...
                </Typography>
              </Box>
            ) : (
              <Stack
                spacing={3}
                component="form"
                onSubmit={handleVerify}
                className={uiStyles.formContent}
              >
                <Stack spacing={1}>
                  <Typography variant="h5" fontWeight={600}>
                    Secure your account
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Complete Google Authenticator setup for {userDetails.email || userDetails.username}.
                  </Typography>
                </Stack>

                {serverError && (
                  <Alert severity="error" onClose={() => setServerError("")}>
                    {serverError}
                  </Alert>
                )}

                {verified && (
                  <Alert
                    icon={<CheckCircleOutline fontSize="inherit" />}
                    severity="success"
                  >
                    Google Authenticator is already enabled for this user.
                  </Alert>
                )}

                <Stack spacing={1.5}>
                  <Typography variant="subtitle2" color="text.primary">
                    How it works
                  </Typography>
                  <Stack spacing={1}>
                    {steps.map((step, index) => (
                      <Stack key={step} direction="row" spacing={1.5} alignItems="flex-start">
                        <Box className={uiStyles.stepBadge}>{index + 1}</Box>
                        <Typography variant="body2" color="text.secondary">
                          {step}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Stack>

                <Divider />

                {qrCode ? (
                  <Box className={uiStyles.qrWrapper}>
                    <img src={qrCode} alt="Google Authenticator QR Code" className={uiStyles.qrImage} />
                  </Box>
                ) : (
                  <Box className={uiStyles.qrPlaceholder}>
                    <Typography variant="body2" color="text.secondary">
                      QR code will appear once the setup link is ready.
                    </Typography>
                  </Box>
                )}

                <Stack spacing={0.5}>
                  <Typography variant="subtitle2">Manual key</Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography className={uiStyles.manualKey}>
                      {manualKey || "Generating..."}
                    </Typography>
                    <IconButton size="small" onClick={handleCopyManualKey} disabled={!manualKey}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>

                <Stack spacing={1}>
                  <Typography variant="subtitle2">Verification code</Typography>
                  <TextField
                    value={verificationCode}
                    onChange={handleCodeChange}
                    placeholder="Enter 6-digit code"
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 6 }}
                    fullWidth
                  />
                </Stack>

                <Stack spacing={1.5}>
                  <CustomButton
                    type="submit"
                    label={verified ? "Confirm Code" : "Enable Two-Factor"}
                    fullWidth
                    disabled={verificationLoading || verificationCode.length !== 6}
                    bgColor={theme.palette.primary.main}
                    hover={theme.palette.primary.light}
                  />
                  <CustomButton
                    label="Back to Login"
                    fullWidth
                    bgColor={theme.palette.background.default}
                    color={theme.palette.primary.main}
                    hover={theme.palette.gray.light}
                    onClick={handleNavigateToLogin}
                  />
                </Stack>
              </Stack>
            )}
          </Card>
        </div>
      </div>
      <div className={layoutStyles.right}>
        <div>
          <LoginSlider />
        </div>
        <p
          className={`${textStyles.normal2} ${textStyles.textWhite} ${textStyles.textCenter}`}
        >
          © 2025 QuantLayer. All rights reserved
        </p>
      </div>
    </div>
  );
};

export default TwoFactorSetupContainer;
